import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core";
import { createPortal } from "react-dom";
import {
  usePlByProgramStyles,
  PlPageHeader,
  PL_FONT,
  PL_TH_BG,
  PL_TH_TEXT,
  PL_TH_BORDER,
  PL_NEG_C,
} from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import { getPsrTrendAPI } from "../../../../api/reports-api";
import { toReportApiFilters } from "../filterRelationshipUtils";
import {
  getApiFilterKey,
  getGlobalPeriodHiddenKeys,
  formatActualsThroughDate,
  headerActualsDate,
  resolvePeriodCellValue,
} from "./plTimeFilterUtils";
import { applyNumFilter, isNumFilterActive, calcOpDropdownPos, renderStandardNumFilter, renderStandardTextFilter } from "./plTableFilterUtils";
import { OpDropdownContent } from "../waterfall-eac/tableColumnFilterShared";
import { calcFilterPos, FILTER_PANEL_WIDTH_NUMERIC } from "../waterfall-eac/filterPopoverUtils";
import Loading from "../../../../common-components/Loading";

import {
  TH_BG_BAND,
  TH_ACTUAL_BG,
  TH_ACTUAL_TEXT,
} from "../../reportTableConfig";

const HAIRLINE     = "#E2E8F0";
const HEADER_TOP   = PL_TH_BG;
const HEADER_ROW   = PL_TH_BG;
const HEADER_LABEL = PL_TH_TEXT;
const FORECAST_C   = "#2563EB";
const TEXT_MUTED   = "#6B7280";
const TEXT_PRIMARY = "#111827";
const PROJ_BG      = "#F0F4F8";
const TOTAL_BG     = "#F0FFF4";
const ROW_STRIPE   = "#F7FAFC";
const ACTUAL_TH_BG = TH_ACTUAL_BG;
const ACTUAL_TH_FG = TH_ACTUAL_TEXT;

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
const DEFAULT_PAGE_SIZE = 10;
const PROJECT_ID_COL_W = 200;
const PERIOD_COL_W = 84;
const TOTAL_PERIOD_COL_W = PERIOD_COL_W + 16;

/* ══════════════════════════════════════════════════════════════ */
/*  Column & row metadata                                         */
/* ══════════════════════════════════════════════════════════════ */
const MONTH_KEYS   = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
const MONTH_LABELS = { jan:"Jan", feb:"Feb", mar:"Mar", apr:"Apr", may:"May", jun:"Jun",
                       jul:"Jul", aug:"Aug", sep:"Sep", oct:"Oct", nov:"Nov", dec:"Dec" };

const ALL_MONTH_COLS = MONTH_KEYS.map((key) => ({ key, label: MONTH_LABELS[key] }));
const ALL_COLS       = [...ALL_MONTH_COLS, { key: "total", label: "Total Year", isTotal: true }];

const ROW_DEFS = [
  { key: "contractRevenue", label: "Revenue",  isPct: false },
  { key: "directLabor",     label: "Direct Labor",      isPct: false },
  { key: "subcontractor",   label: "Subcontractor",     isPct: false },
  { key: "odcs",            label: "ODCs",              isPct: false },
  { key: "totalDirectCost", label: "Total Direct Cost", isPct: false },
  { key: "grossMargin",     label: "Gross Margin",      isPct: false },
  { key: "grossMarginPct",  label: "Gross Margin %",    isPct: true  },
  { key: "indirectCost",    label: "Indirect Cost",     isPct: false },
  { key: "totalCost",       label: "Total Cost",        isPct: false },
  { key: "netProfit",       label: "Net Profit / Fee",  isPct: false },
  { key: "netMarginPct",    label: "Net Margin %",      isPct: true  },
];

/* ══════════════════════════════════════════════════════════════ */
/*  Filter helpers — see plTableFilterUtils.js                    */
/* ══════════════════════════════════════════════════════════════ */

/* ══════════════════════════════════════════════════════════════ */
/*  Number formatter                                              */
/* ══════════════════════════════════════════════════════════════ */
const fmtCell = (v, isPct) => {
  if (v == null) return "—";
  const n = +v;
  if (!Number.isFinite(n)) return "—";
  if (isPct) {
    const abs = `${Math.abs(n).toFixed(1)}%`;
    return n < 0 ? `(${abs})` : abs;
  }
  const abs = `$${Math.abs(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  return n < 0 ? `(${abs})` : abs;
};

const cellColor = (v, fallback) => (v != null && Number.isFinite(+v) && +v < 0 ? PL_NEG_C : fallback);

/* ══════════════════════════════════════════════════════════════ */
/*  Data helpers                                                  */
/* ══════════════════════════════════════════════════════════════ */
function normalizeRows(rows) {
  const out = { ...rows };
  ROW_DEFS.forEach(({ key, isPct }) => {
    if (isPct && out[key]) {
      const t = {};
      ALL_COLS.forEach(({ key: ck }) => { t[ck] = (out[key][ck] ?? 0) * 100; });
      out[key] = t;
    }
  });
  return out;
}

function aggregatePsrGrandTotals(projects, rowDefs, colKeys) {
  const raw = {};
  for (const { key, isPct } of rowDefs) {
    if (isPct) continue;
    raw[key] = {};
    for (const ck of colKeys) {
      raw[key][ck] = projects.reduce((s, p) => s + (p.rows?.[key]?.[ck] ?? 0), 0);
    }
  }
  const rev = raw.contractRevenue || {};
  const gm = raw.grossMargin || {};
  const np = raw.netProfit || {};
  raw.grossMarginPct = {};
  raw.netMarginPct = {};
  for (const ck of colKeys) {
    raw.grossMarginPct[ck] = rev[ck] ? gm[ck] / rev[ck] : 0;
    raw.netMarginPct[ck] = rev[ck] ? np[ck] / rev[ck] : 0;
  }
  return normalizeRows(raw);
}

function buildApiFilters(filters) {
  return toReportApiFilters(filters, [
    "year", "orgId", "contractType", "contractId", "clinLvl2", "businessType", "awardType",
    "competitionType", "bu", "popEndDate", "sector",
    "contractName", "customerName",
  ]);
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  tableSection: {
    marginBottom: 24,
    background: "#FFFFFF",
    borderRadius: 12,
    border: "1px solid #EBEBEB",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
    overflow: "visible",
  },
  tableSectionHead: {
    padding: "16px 20px 14px 24px",
    borderBottom: `1px solid ${HAIRLINE}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    position: "relative",
    zIndex: 20,
    overflow: "visible",
  },
  tableSectionHeadText: { minWidth: 0 },
  tableSectionTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: TEXT_PRIMARY,
    letterSpacing: "-0.02em",
    lineHeight: 1.35,
    fontFamily: PL_FONT,
    margin: 0,
  },
  tableSectionMeta: {
    marginTop: 4,
    fontSize: 12,
    fontWeight: 400,
    color: TEXT_MUTED,
    fontFamily: PL_FONT,
    lineHeight: 1.4,
  },
  /* Single scroll container — one scrollbar for header + body + footer */
  tableOuter: {
    overflowX: "auto",
    overflowY: "visible",
  },
  footerDivider: {
    borderTop: `2px solid ${HAIRLINE}`,
  },
  table: {
    borderCollapse: "collapse",
    fontFamily: PL_FONT,
    fontSize: 13,
    tableLayout: "fixed",
    width: "max-content",
    minWidth: "100%",
  },

  /* ── Band header row (row 1) ── */
  thBandCorner: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "12px 16px", fontWeight: 700, fontSize: 10,
    letterSpacing: "0.14em", textTransform: "uppercase",
    textAlign: "left", verticalAlign: "middle", whiteSpace: "nowrap",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  thBandMetric: {
    background: HEADER_TOP, color: HEADER_LABEL,
    padding: "12px 16px", fontWeight: 700, fontSize: 10,
    letterSpacing: "0.14em", textTransform: "uppercase",
    textAlign: "center", verticalAlign: "middle", whiteSpace: "nowrap",
    borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}`,
  },
  toggleAllBtn: {
    display: "inline-flex", alignItems: "center", gap: 7,
    background: "none", border: "none", padding: 0, margin: 0,
    cursor: "pointer", color: HEADER_LABEL, fontFamily: PL_FONT,
    fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase",
    opacity: 0.9, transition: "opacity 0.12s",
    "&:hover": { opacity: 1 },
  },
  toggleAllIcon: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 20, height: 20, borderRadius: "50%",
    background: "#fff", color: "#4F46E5",
    flexShrink: 0, boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
  },
  thBandActuals:  { background: HEADER_TOP, color: HEADER_LABEL, padding: "11px 4px", fontWeight: 600, fontSize: 10, textAlign: "center", letterSpacing: "0.10em", textTransform: "uppercase", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thBandForecast: { background: HEADER_TOP, color: HEADER_LABEL, padding: "11px 4px", fontWeight: 600, fontSize: 10, textAlign: "center", letterSpacing: "0.10em", textTransform: "uppercase", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },
  thBandTotalYear:{ background: HEADER_TOP, color: HEADER_LABEL, padding: "11px 4px", fontWeight: 700, fontSize: 10, textAlign: "center", letterSpacing: "0.10em", textTransform: "uppercase", verticalAlign: "middle", borderBottom: `1px solid ${PL_TH_BORDER}`, borderRight: `1px solid ${PL_TH_BORDER}` },

  /* ── Column header row (row 2) ── */
  thColActual:   {
    background: ACTUAL_TH_BG, color: ACTUAL_TH_FG, padding: "11px 4px", fontWeight: 600, fontSize: 12,
    textAlign: "center", whiteSpace: "nowrap", borderRight: `1px solid ${PL_TH_BORDER}`,
    "& $filterIconBtn": {
      color: ACTUAL_TH_FG, opacity: 0.65,
      "&:hover": { opacity: 1, background: "rgba(0,0,0,0.08)" },
    },
    "& $filterIconBtnActive": {
      opacity: 1, color: ACTUAL_TH_FG, background: "rgba(0,0,0,0.12)",
    },
  },
  thColForecast: { background: HEADER_ROW, color: HEADER_LABEL, padding: "11px 4px", fontWeight: 600, fontSize: 12, textAlign: "center", whiteSpace: "nowrap", borderRight: `1px solid ${PL_TH_BORDER}` },
  thColTotal:    { background: HEADER_ROW, color: HEADER_LABEL, padding: "11px 4px", fontWeight: 600, fontSize: 12, textAlign: "center", whiteSpace: "nowrap", borderRight: `1px solid ${PL_TH_BORDER}` },

  /* ── Floating filter row ── */
  trFilter: {
    background: "#F1F5F9",
    borderBottom: `2px solid ${HAIRLINE}`,
  },
  filterLabelCell: { padding: "5px 10px", background: "#F1F5F9", verticalAlign: "middle" },
  filterNumCell:   { padding: "5px 4px",  background: "#F1F5F9", verticalAlign: "middle" },
  filterWrap: {
    display: "flex", alignItems: "center", gap: 3, height: 26,
  },
  filterLabelStack: {
    display: "flex", flexDirection: "column", gap: 4,
  },
  filterMetricSelect: {
    height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5,
    fontSize: 11, padding: "0 6px", outline: "none", fontFamily: PL_FONT,
    background: "#fff", color: "#374151", cursor: "pointer", width: "100%",
    "&:focus": { borderColor: "#4F46E5" },
  },
  filterTextInput: {
    flex: 1, height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5,
    fontSize: 11, padding: "0 7px", outline: "none", fontFamily: PL_FONT,
    background: "#fff", color: "#111827",
    "&:focus": { borderColor: "#4F46E5" },
    "&::placeholder": { color: "#9CA3AF" },
  },
  filterNumInput: {
    width: "100%", height: 26, border: "1.5px solid #CBD5E1", borderRadius: 5,
    fontSize: 11, padding: "0 5px", outline: "none", fontFamily: PL_FONT,
    background: "#fff", color: "#111827", textAlign: "right",
    "&:focus": { borderColor: "#4F46E5" },
    "&::placeholder": { color: "#CBD5E1" },
  },
  filterOpBtn: {
    flexShrink: 0, width: 22, height: 26,
    border: "1.5px solid #CBD5E1", borderRadius: 5,
    background: "#fff", fontSize: 11, fontWeight: 700, color: "#4F46E5",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    padding: 0, fontFamily: PL_FONT,
    "&:hover": { background: "#EEF2FF", borderColor: "#4F46E5" },
  },
  filterOpBtnActive: {
    background: "#4F46E5", borderColor: "#4F46E5", color: "#fff",
    "&:hover": { background: "#4338CA" },
  },
  filterClearBtn: {
    flexShrink: 0, width: 18, height: 18, border: "none", borderRadius: "50%",
    background: "#E2E8F0", fontSize: 9, color: "#64748B",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    padding: 0,
    "&:hover": { background: "#CBD5E1" },
  },
  opDropdown: {
    position: "fixed", zIndex: 100, background: "#fff",
    border: "1px solid #E2E8F0", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 190,
    overflow: "hidden", fontFamily: PL_FONT,
  },
  opDropdownItem: {
    display: "block", width: "100%", padding: "8px 14px",
    fontSize: 12, color: "#374151", background: "none", border: "none",
    textAlign: "left", cursor: "pointer", fontFamily: PL_FONT,
    "&:hover": { background: "#EEF2FF", color: "#4F46E5" },
  },
  opDropdownItemActive: { background: "#EEF2FF", color: "#4F46E5", fontWeight: 600 },

  /* ── Column picker ── */
  colPickerWrap: { position: "relative", flexShrink: 0 },
  colsBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    height: 30, padding: "0 12px", borderRadius: 6,
    border: "1px solid #E2E8F0", background: "#fff",
    fontSize: 12, fontWeight: 500, color: "#374151",
    cursor: "pointer", fontFamily: PL_FONT,
    transition: "border-color 0.15s, background 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#C7D2FE" },
  },
  colsBtnActive: { borderColor: "#C7D2FE", background: "#EEF2FF", color: "#4F46E5" },
  hiddenBadge: {
    display: "inline-flex", alignItems: "center", height: 18,
    padding: "0 7px", borderRadius: 10, background: "#4F46E5",
    color: "#fff", fontSize: 11, fontWeight: 600, lineHeight: 1,
  },
  colPicker: {
    position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 1200,
    background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10,
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)", minWidth: 180,
    maxHeight: 420, overflowY: "auto", fontFamily: PL_FONT,
  },
  colPickerHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 14px 8px", borderBottom: "1px solid #F3F4F6",
  },
  colPickerTitle: { fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.05em", textTransform: "uppercase" },
  colPickerReset: {
    fontSize: 11, fontWeight: 600, color: "#4F46E5",
    background: "none", border: "none", cursor: "pointer", padding: 0,
    fontFamily: PL_FONT, "&:hover": { textDecoration: "underline" },
  },
  colPickerItem: {
    display: "flex", alignItems: "center", gap: 9, padding: "7px 14px",
    cursor: "pointer", fontSize: 12, color: "#374151", userSelect: "none",
    borderBottom: "1px solid #F9FAFB",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { background: "#F9FAFB" },
  },
  colPickerCheck: { width: 14, height: 14, accentColor: "#4F46E5", cursor: "pointer", flexShrink: 0 },

  filterIconBtn: {
    flexShrink: 0, width: 16, height: 16, border: "none",
    background: "transparent", cursor: "pointer", padding: 0,
    borderRadius: 3, color: PL_TH_TEXT, opacity: 0.5,
    display: "flex", alignItems: "center", justifyContent: "center",
    "&:hover": { opacity: 1, background: "rgba(165,180,252,0.18)", color: "#A5B4FC" },
  },
  filterIconBtnActive: {
    opacity: 1, color: "#A5B4FC",
    background: "rgba(165,180,252,0.18)",
  },
  filterPanel: {
    position: "fixed", zIndex: 99, background: "#fff",
    border: "1px solid #E2E8F0", borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    padding: "10px 12px", minWidth: 200, fontFamily: PL_FONT,
  },

  /* ── Project separator row ── */
  tdProjectRow: {
    cursor: "pointer", userSelect: "none",
    "& > td": { transition: "background 0.12s" },
    "&:hover > td": { background: "#E4EAF2 !important" },
  },
  tdProjectId: {
    padding: "10px 16px", fontWeight: 700, fontSize: 12,
    color: TEXT_PRIMARY, background: PROJ_BG,
    borderBottom: `1px solid ${HAIRLINE}`, whiteSpace: "nowrap",
  },
  tdProjectBlank: { background: PROJ_BG, borderBottom: `1px solid ${HAIRLINE}` },

  /* ── Metric data rows ── */
  tdMetricLabel: {
    padding: "9px 20px 9px 32px", fontWeight: 400, color: TEXT_MUTED,
    whiteSpace: "nowrap", borderBottom: `1px solid ${HAIRLINE}`,
    fontSize: 12, background: "#FFFFFF",
  },
  tdMetricCell: {
    padding: "9px 4px", textAlign: "right", color: TEXT_MUTED,
    borderBottom: `1px solid ${HAIRLINE}`, fontSize: 12,
    whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", background: "#FFFFFF",
  },
  tdMetricTotal: {
    padding: "9px 4px", textAlign: "right",
    borderBottom: `1px solid ${HAIRLINE}`, fontSize: 12,
    whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
    background: "#FFFFFF", color: TEXT_PRIMARY, fontWeight: 700,
  },

  /* ── Grand-total footer ── */
  tdFootLabel: {
    padding: "12px 4px 12px 20px", fontWeight: 700, color: TEXT_PRIMARY,
    whiteSpace: "nowrap", fontSize: 12, background: TOTAL_BG,
    borderBottom: `1px solid ${HAIRLINE}`,
  },
  tdFootCell: {
    padding: "12px 4px", textAlign: "right", fontSize: 12,
    whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
    background: TOTAL_BG, borderBottom: `1px solid ${HAIRLINE}`, fontWeight: 600,
  },
  tdFootTotal: {
    padding: "12px 4px", textAlign: "right", fontSize: 13,
    whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
    background: TOTAL_BG, borderBottom: `1px solid ${HAIRLINE}`,
    fontWeight: 700, color: TEXT_PRIMARY,
  },

  /* ── Pagination bar ── */
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 20px", borderTop: `1px solid ${HAIRLINE}`,
    background: "#fff", flexWrap: "wrap", gap: 10,
  },
  paginationInfo: {
    display: "flex", alignItems: "center", gap: 8,
    fontSize: 13, color: TEXT_MUTED, fontFamily: PL_FONT,
  },
  pageSizeLabel: { fontSize: 13, color: TEXT_MUTED, fontFamily: PL_FONT, whiteSpace: "nowrap" },
  pageSizeSelectWrap: {
    position: "relative", display: "inline-flex", alignItems: "center",
  },
  pageSizeSelect: {
    appearance: "none", WebkitAppearance: "none",
    height: 32, padding: "0 28px 0 10px", borderRadius: 7,
    border: "1px solid #D1D5DB", background: "#fff",
    fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY, fontFamily: PL_FONT,
    cursor: "pointer", outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: "#6B7280" },
  },
  pageSizeChevron: {
    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
    pointerEvents: "none", color: "#6B7280",
  },
  paginationControls: {
    display: "flex", alignItems: "center", gap: 3,
  },
  pageBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 32, height: 32, padding: "0 6px", borderRadius: 7,
    border: "1px solid #E5E7EB", background: "#fff",
    fontSize: 13, fontWeight: 500, color: TEXT_PRIMARY,
    cursor: "pointer", fontFamily: PL_FONT, lineHeight: 1,
    transition: "background 0.12s, border-color 0.12s, color 0.12s",
    "&:hover:not(:disabled)": { background: "#F3F4F6", borderColor: "#D1D5DB" },
    "&:disabled": { opacity: 0.35, cursor: "default" },
  },
  pageBtnActive: {
    background: PL_TH_BG, borderColor: PL_TH_BG, color: "#fff",
    fontWeight: 600,
    "&:hover:not(:disabled)": { background: TH_BG_BAND, borderColor: TH_BG_BAND, color: "#fff" },
  },
  pageEllipsis: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 32, height: 32, fontSize: 13, color: TEXT_MUTED, fontFamily: PL_FONT,
    letterSpacing: "0.05em",
  },
}));

/* ── Chevron ── */
function ChevronIcon({ open }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"
      style={{ transition: "transform 0.18s ease", transform: open ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}
    >
      <path d="M3 2l4 3-4 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Dynamic ColGroup ── */
function ColGroup({ visibleMetricGroups, visiblePeriods, colWidths }) {
  const periodW = colWidths?.period ?? PERIOD_COL_W;
  return (
    <colgroup>
      <col style={{ width: colWidths?.projectId ?? PROJECT_ID_COL_W }} />
      {visibleMetricGroups.flatMap((metric) =>
        visiblePeriods.map((pk) => (
          <col
            key={`${metric.key}-${pk}`}
            style={{ width: pk === "total" ? TOTAL_PERIOD_COL_W : periodW }}
          />
        )),
      )}
    </colgroup>
  );
}

function metricPeriodFilterKey(metricKey, periodKey) {
  return `${metricKey}::${periodKey}`;
}

function parseMetricPeriodFilterKey(filterKey) {
  const sep = filterKey.indexOf("::");
  if (sep === -1) return { metricKey: null, periodKey: filterKey };
  return {
    metricKey: filterKey.slice(0, sep),
    periodKey: filterKey.slice(sep + 2),
  };
}

function stickyLeftStyle(bg, zIndex = 2) {
  return {
    position: "sticky",
    left: 0,
    zIndex,
    background: bg,
    boxShadow: "6px 0 12px -8px rgba(0,0,0,0.08)",
  };
}

/* ── Pagination page-number buttons with ellipsis ── */
function PageButtons({ page, totalPages, onPageChange, classes }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const delta = 1; // pages around current

  // Always show first, last, current ± delta, with ellipsis gaps
  const ranges = new Set([0, totalPages - 1]);
  for (let i = Math.max(0, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
    ranges.add(i);
  }
  const sorted = [...ranges].sort((a, b) => a - b);

  let prev = -1;
  sorted.forEach((p) => {
    if (prev !== -1 && p - prev > 1) pages.push("…");
    pages.push(p);
    prev = p;
  });

  return pages.map((p, i) =>
    p === "…" ? (
      <span key={`ellipsis-${i}`} className={classes.pageEllipsis}>…</span>
    ) : (
      <button
        key={p}
        className={`${classes.pageBtn} ${p === page ? classes.pageBtnActive : ""}`}
        onClick={() => onPageChange(p)}
      >
        {p + 1}
      </button>
    )
  );
}

function FilterFunnelIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main component                                                */
/* ══════════════════════════════════════════════════════════════ */
export default function PSRTrend({ filters, actualsThroughDate }) {
  const plClasses    = usePlByProgramStyles();
  const localClasses = useStyles();
  useReportFonts();
  const classes = { ...plClasses, ...localClasses };

  /* ── API state ── */
  const [projects,    setProjects]    = useState([]);
  const [grandTotals, setGrandTotals] = useState({});
  const [actualCols,  setActualCols]  = useState(new Set());
  const [fiscalYear,  setFiscalYear]  = useState(null);
  const [actualThroughMonth, setActualThroughMonth] = useState(null);
  const [liveVersion, setLiveVersion] = useState(null);
  const [loading,     setLoading]     = useState(true);

  /* ── Pagination state ── */
  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  /* ── UI state ── */
  const [hiddenCols,    setHiddenCols]    = useState(new Set());
  const [hiddenRows,    setHiddenRows]    = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const [projFilter,    setProjFilter]    = useState("");
  const [colFilters,    setColFilters]    = useState({});
  const [openOpKey,     setOpenOpKey]     = useState(null);
  const [dropdownPos,   setDropdownPos]   = useState({ top: 0, left: 0 });
  const [openFilterKey,  setOpenFilterKey]  = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });

  const colPickerWrapRef = useRef(null);
  const opBtnRefs        = useRef({});
  const filterBtnRefs    = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef     = useRef(null);

  const apiFilterKey = useMemo(() => getApiFilterKey(filters, buildApiFilters), [filters]);
  const globalPeriodHidden = useMemo(
    () => getGlobalPeriodHiddenKeys(filters, "Month", MONTH_KEYS),
    [filters?.monthName, filters?.quarter],
  );

  useEffect(() => { openFilterKeyRef.current = openFilterKey; }, [openFilterKey]);
  useEffect(() => { openOpKeyRef.current     = openOpKey;     }, [openOpKey]);

  const repositionPanels = useCallback(() => {
    const key = openFilterKeyRef.current;
    if (key) {
      const btn = filterBtnRefs.current[key];
      if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    }
    const opKey = openOpKeyRef.current;
    if (opKey) {
      const opBtn = opBtnRefs.current[opKey];
      if (opBtn) setDropdownPos(calcOpDropdownPos(opBtn));
    }
  }, []);

  const handleTableScroll = useCallback(() => {
    repositionPanels();
  }, [repositionPanels]);

  /* Keep filter popovers anchored below header buttons during page/table scroll */
  useEffect(() => {
    window.addEventListener("scroll", repositionPanels, true);
    window.addEventListener("resize", repositionPanels);
    return () => {
      window.removeEventListener("scroll", repositionPanels, true);
      window.removeEventListener("resize", repositionPanels);
    };
  }, [repositionPanels]);

  /* ── Fetch — reset page on new data ── */
  useEffect(() => {
    setLoading(true);
    setPage(0);
    setProjFilter("");
    setColFilters({});
    getPsrTrendAPI(buildApiFilters(filters))
      .then((res) => {
        if (res?.status === 200 && res.data) {
          const { projects: projs, grandTotals: gt, actualThroughMonth, fiscalYear: fy, liveVersion: lv } = res.data;
          const actSet = new Set(MONTH_KEYS.slice(0, actualThroughMonth ?? 0));
          setProjects((projs || []).map((p) => ({ ...p, rows: normalizeRows(p.rows) })));
          setGrandTotals(normalizeRows(gt || {}));
          setActualCols(actSet);
          setFiscalYear(fy ?? null);
          setActualThroughMonth(actualThroughMonth ?? null);
          setLiveVersion(lv ?? null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Reset page when page size changes ── */
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(0);
  };

  /* ── Pagination derived values ── */
  const filteredProjects = projects.filter((p) => {
    if (projFilter && !p.projectId.toLowerCase().includes(projFilter.toLowerCase())) return false;
    for (const [filterKey, filter] of Object.entries(colFilters)) {
      if (!isNumFilterActive(filter)) continue;
      const { metricKey, periodKey } = parseMetricPeriodFilterKey(filterKey);
      if (!metricKey || !periodKey) continue;
      const metricRow = p.rows[metricKey] || {};
      if (!applyNumFilter(metricRow[periodKey] ?? 0, filter)) return false;
    }
    return true;
  });
  const totalPages    = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
  const safePage      = Math.min(page, totalPages - 1);
  const pagedProjects = filteredProjects.slice(safePage * pageSize, (safePage + 1) * pageSize);
  const rangeStart    = filteredProjects.length === 0 ? 0 : safePage * pageSize + 1;
  const rangeEnd      = Math.min((safePage + 1) * pageSize, filteredProjects.length);

  const goToPage = (p) => {
    setPage(Math.max(0, Math.min(p, totalPages - 1)));
  };

  /* ── Close pickers on outside click ── */
  useEffect(() => {
    if (!colPickerOpen) return;
    const h = (e) => {
      if (colPickerWrapRef.current && !colPickerWrapRef.current.contains(e.target))
        setColPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [colPickerOpen]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]") && !e.target.closest("[data-op-btn]"))
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    if (!openFilterKey) return;
    const h = (e) => {
      if (!e.target.closest("[data-filter-panel]") && !e.target.closest("[data-filter-btn]") && !e.target.closest("[data-op-dd]") && !e.target.closest("[data-op-dropdown]"))
        setOpenFilterKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey]);

  /* ── Derived: visible columns ── */
  /* A selected global month/quarter filter overrides the table-level column
     picker — e.g. hiding Jan via the picker then selecting Jan globally
     should bring the Jan column back, not keep it hidden. */
  const isColHidden       = (key) => {
    if (globalPeriodHidden.has(key)) return true;
    if (globalPeriodHidden.size > 0) return false;
    return hiddenCols.has(key);
  };
  const visibleMonthCols  = ALL_MONTH_COLS.filter((c) => !isColHidden(c.key));
  const visibleCols       = ALL_COLS.filter((c) => !isColHidden(c.key));
  const showTotal         = !isColHidden("total");
  const hiddenCount       = hiddenCols.size + hiddenRows.size;
  const periodCellOpts = (rowKey, rowData, isPct, allRowData) => ({
    rowKey,
    allRowData,
    isPct,
  });

  const getTotalValue = (rowKey, rowData, isPct, allRowData) =>
    resolvePeriodCellValue(rowData, "total", filters, "Month", periodCellOpts(rowKey, rowData, isPct, allRowData));

  const hasTableFilters = projFilter || Object.values(colFilters).some((f) => isNumFilterActive(f));
  const displayGrandTotals = useMemo(() => {
    if (!hasTableFilters) return grandTotals;
    return aggregatePsrGrandTotals(
      filteredProjects,
      ROW_DEFS,
      ALL_COLS.map((c) => c.key),
    );
  }, [hasTableFilters, filteredProjects, grandTotals]);

  const visiblePeriods = [
    ...visibleMonthCols.map((c) => c.key),
    ...(showTotal ? ["total"] : []),
  ];

  /* ── Column picker (metrics + months) ── */
  const toggleCol   = (key) => setHiddenCols((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const visibleRowDefs  = ROW_DEFS.filter((r) => !hiddenRows.has(r.key));
  const toggleRow       = (key) => setHiddenRows((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const showAllCols = () => {
    setHiddenCols(new Set());
    setHiddenRows(new Set());
  };

  /* ── Filter handlers ── */
  const updateColFilter = useCallback((colKey, field, value) =>
    setColFilters((prev) => ({ ...prev, [colKey]: { op: "gt", ...prev[colKey], [field]: value } })), []);

  const clearColFilter = useCallback((colKey) =>
    setColFilters((prev) => { const n = { ...prev }; delete n[colKey]; return n; }), []);

  const isColFilterActive = (filterKey) => isNumFilterActive(colFilters[filterKey]);

  const toggleOpDropdown = (colKey) => {
    if (openOpKey === colKey) { setOpenOpKey(null); return; }
    const btn = opBtnRefs.current[colKey];
    if (btn) setDropdownPos(calcOpDropdownPos(btn));
    setOpenOpKey(colKey);
  };

  const selectOp = (colKey, opKey) => { updateColFilter(colKey, "op", opKey); setOpenOpKey(null); };

  const hasFilterValue = (key) => {
    if (key === "proj") return !!projFilter;
    return isColFilterActive(key);
  };

  const toggleFilter = (key) => {
    if (openFilterKey === key) { setOpenFilterKey(null); return; }
    const btn = filterBtnRefs.current[key];
    if (btn) setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
    setOpenFilterKey(key);
    setOpenOpKey(null);
  };

  const renderNumFilterInPanel = (filterKey) => {
    const { metricKey, periodKey } = parseMetricPeriodFilterKey(filterKey);
    const metricLabel = ROW_DEFS.find((r) => r.key === metricKey)?.label || metricKey;
    const periodLabel =
      ALL_COLS.find((c) => c.key === periodKey)?.label || MONTH_LABELS[periodKey] || periodKey;
    return renderStandardNumFilter({
      label: `${metricLabel} · ${periodLabel}`,
      colKey: filterKey,
      colFilters,
      updateColFilter: (key, field, value) => { updateColFilter(key, field, value); setPage(0); },
      clearColFilter: (key) => { clearColFilter(key); setPage(0); },
      toggleOpDropdown,
      opBtnRefs,
    });
  };

  const renderFilterContent = (key) => {
    if (key === "proj") {
      return renderStandardTextFilter({ label: "Project ID", value: projFilter, onChange: (v) => { setProjFilter(v); setPage(0); }, onClear: () => { setProjFilter(""); setPage(0); }, placeholder: "Search project ID…" });
    }
    return renderNumFilterInPanel(key);
  };

  const fyLabel    = fiscalYear ? `FY-${fiscalYear}` : "FY";
  const lastActual = [...visibleMonthCols].reverse().find((c) => actualCols.has(c.key));
  const liveYear = new Date().getFullYear();
  const headerDate = headerActualsDate(
    filters?.year,
    loading,
    formatActualsThroughDate(actualThroughMonth),
  );

  if (loading) return <Loading />;

  return (
    <div className={classes.root}>
      <div className={classes.container}>

        <PlPageHeader
          classes={classes}
          eyebrow="P&L by Program"
          title="PSR Trend"
          accent="Monthly Performance"
          meta={`Program Summary · Monthly Actuals & Forecast by Project · ${projects.length} Projects`}
          metaTop={lastActual ? `Actuals through ${lastActual.label} – ${liveYear}` : fyLabel}
          date={headerDate}
        />

        <div className={classes.tableSection}>
          {/* ── Section head ── */}
          <div className={classes.tableSectionHead}>
            <div className={classes.tableSectionHeadText}>
              <h2 className={classes.tableSectionTitle}>Project Breakdown</h2>
              <div className={classes.tableSectionMeta}>
                {fyLabel} · {filteredProjects.length}{projFilter ? ` of ${projects.length}` : ""} Projects · {visibleRowDefs.length} of {ROW_DEFS.length} Metrics
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div className={classes.colPickerWrap} ref={colPickerWrapRef}>
                <button
                  className={`${classes.colsBtn} ${colPickerOpen ? classes.colsBtnActive : ""}`}
                  onClick={() => setColPickerOpen((p) => !p)}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="18"/><rect x="14" y="3" width="7" height="18"/>
                  </svg>
                  Columns
                  {hiddenCount > 0 && <span className={classes.hiddenBadge}>{hiddenCount} Hidden</span>}
                </button>
                {colPickerOpen && (
                  <div className={classes.colPicker}>
                    <div className={classes.colPickerHeader}>
                      <span className={classes.colPickerTitle}>Metrics</span>
                    </div>
                    {ROW_DEFS.map((row) => (
                      <label key={row.key} className={classes.colPickerItem}>
                        <input type="checkbox" className={classes.colPickerCheck}
                          checked={!hiddenRows.has(row.key)} onChange={() => toggleRow(row.key)} />
                        {row.label}
                      </label>
                    ))}
                    <div className={classes.colPickerHeader} style={{ marginTop: 4 }}>
                      <span className={classes.colPickerTitle}>Months</span>
                    </div>
                    {ALL_COLS.map((col) => (
                      <label key={col.key} className={classes.colPickerItem} style={globalPeriodHidden.has(col.key) ? { opacity: 0.45 } : undefined}>
                        <input type="checkbox" className={classes.colPickerCheck}
                          checked={!isColHidden(col.key)} disabled={globalPeriodHidden.has(col.key)} onChange={() => toggleCol(col.key)} />
                        {col.label}
                      </label>
                    ))}
                    {hiddenCount > 0 && (
                      <button type="button" className={classes.colPickerReset} style={{ padding: "10px 14px" }} onClick={showAllCols}>
                        Show All Columns
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Single scroll container — one scrollbar for all three sections ── */}
          <div className={classes.tableOuter} onScroll={handleTableScroll}>
            <table className={classes.table}>
              <ColGroup
                visibleMetricGroups={visibleRowDefs}
                visiblePeriods={visiblePeriods}
                colWidths={{ projectId: PROJECT_ID_COL_W, period: PERIOD_COL_W }}
              />

              <thead>
                <tr>
                  {/* Sticky Project ID */}
                  <th
                    className={classes.thBandCorner}
                    rowSpan={2}
                    style={stickyLeftStyle(HEADER_TOP, 5)}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 4,
                      }}
                    >
                      <span style={{ letterSpacing: "0.14em" }}>
                        Project ID
                      </span>
                      <button
                        ref={(el) => {
                          filterBtnRefs.current["proj"] = el;
                        }}
                        data-filter-btn
                        className={`${classes.filterIconBtn} ${hasFilterValue("proj") ? classes.filterIconBtnActive : ""}`}
                        onClick={() => toggleFilter("proj")}
                        title="Filter"
                      >
                        <FilterFunnelIcon />
                      </button>
                    </div>
                  </th>

                  {/* Metric group band */}
                  {visibleRowDefs.map((metric) => (
                    <th
                      key={metric.key}
                      className={classes.thBandMetric}
                      colSpan={visiblePeriods.length}
                    >
                      {metric.label}
                    </th>
                  ))}
                </tr>

                {/* Period headers (months + optional Total Year) */}
                <tr>
                  {visibleRowDefs.map((metric) =>
                    visiblePeriods.map((pk) => {
                      const isTotal = pk === "total";
                      const isActual = !isTotal && actualCols.has(pk);
                      const label = isTotal ? "Total Year" : (MONTH_LABELS[pk] ?? pk);
                      const filterKey = metricPeriodFilterKey(metric.key, pk);
                      const cellClass = isTotal
                        ? classes.thColTotal
                        : isActual
                          ? classes.thColActual
                          : classes.thColForecast;
                      return (
                        <th
                          key={`${metric.key}-${pk}`}
                          className={cellClass}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                              padding: "0 2px",
                            }}
                          >
                            <span
                              style={{
                                color: isActual
                                  ? ACTUAL_TH_FG
                                  : openFilterKey === filterKey
                                    ? "#60A5FA"
                                    : undefined,
                              }}
                            >
                              {label}
                            </span>

                            <button
                              ref={(el) => {
                                filterBtnRefs.current[filterKey] = el;
                              }}
                              data-filter-btn
                              className={`${classes.filterIconBtn} ${hasFilterValue(filterKey) ? classes.filterIconBtnActive : ""}`}
                              onClick={() => toggleFilter(filterKey)}
                              title="Filter"
                            >
                              <FilterFunnelIcon />
                            </button>
                          </div>
                        </th>
                      );
                    })
                  )}
                </tr>
              </thead>

              <tbody>
                {pagedProjects.map((proj, ri) => {
                  const rowBg = ri % 2 === 0 ? "#FFFFFF" : ROW_STRIPE;
                  return (
                    <tr key={proj.projectId}>
                      <td
                        className={classes.tdProjectId}
                        style={stickyLeftStyle(rowBg, 3)}
                      >
                        {proj.projectId}
                      </td>

                      {visibleRowDefs.map((metric) => {
                        const rowData = proj.rows[metric.key] || {};
                        return visiblePeriods.map((pk) => {
                          const isTotal = pk === "total";
                          const val = isTotal
                            ? getTotalValue(metric.key, rowData, metric.isPct, proj.rows)
                            : rowData[pk];
                          const color = isTotal
                            ? cellColor(val, TEXT_PRIMARY)
                            : cellColor(
                                val,
                                actualCols.has(pk) ? TEXT_PRIMARY : FORECAST_C,
                              );

                          return (
                            <td
                              key={`${proj.projectId}-${metric.key}-${pk}`}
                              className={classes.tdMetricCell}
                              style={{
                                background: rowBg,
                                color,
                                fontStyle: metric.isPct
                                  ? "italic"
                                  : "normal",
                              }}
                            >
                              {fmtCell(val, metric.isPct)}
                            </td>
                          );
                        });
                      })}
                    </tr>
                  );
                })}
              </tbody>

              {/* Grand-total footer (single row) */}
              <tfoot className={classes.footerDivider}>
                <tr>
                  <td
                    className={classes.tdFootLabel}
                    style={{ fontStyle: "normal", ...stickyLeftStyle(TOTAL_BG, 4) }}
                  >
                    Total
                  </td>

                  {visibleRowDefs.map((metric) => {
                    const tData = displayGrandTotals[metric.key] || {};
                    return visiblePeriods.map((pk) => {
                      const isTotal = pk === "total";
                      const val = isTotal
                        ? getTotalValue(metric.key, tData, metric.isPct, displayGrandTotals)
                        : tData[pk];
                      const color = isTotal
                        ? cellColor(val, TEXT_PRIMARY)
                        : cellColor(
                            val,
                            actualCols.has(pk) ? TEXT_PRIMARY : FORECAST_C,
                          );

                      const italic = metric.isPct ? "italic" : "normal";
                      const TdClass = isTotal ? classes.tdFootTotal : classes.tdFootCell;

                      return (
                        <td
                          key={`total-${metric.key}-${pk}`}
                          className={TdClass}
                          style={{ color, fontStyle: italic }}
                        >
                          {fmtCell(val, metric.isPct)}
                        </td>
                      );
                    });
                  })}
                </tr>
              </tfoot>

            </table>
          </div>

          {/* ── Pagination bar ── */}
          <div className={classes.pagination}>
            <div className={classes.paginationInfo}>
              <span className={classes.pageSizeLabel}>Rows Per Page</span>
              <div className={classes.pageSizeSelectWrap}>
                <select className={classes.pageSizeSelect} value={pageSize} onChange={handlePageSizeChange}>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <span className={classes.pageSizeChevron}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 4.5L6 8l3.5-3.5" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </div>

            <div className={classes.paginationControls}>
              <button className={classes.pageBtn} onClick={() => goToPage(0)} disabled={safePage === 0}
                title="First Page">«</button>
              <button className={classes.pageBtn} onClick={() => goToPage(safePage - 1)} disabled={safePage === 0}
                title="Previous Page">‹</button>

              <PageButtons page={safePage} totalPages={totalPages} onPageChange={goToPage} classes={classes} />

              <button className={classes.pageBtn} onClick={() => goToPage(safePage + 1)} disabled={safePage >= totalPages - 1}
                title="Next Page">›</button>
              <button className={classes.pageBtn} onClick={() => goToPage(totalPages - 1)} disabled={safePage >= totalPages - 1}
                title="Last Page">»</button>
            </div>
          </div>

        </div>

        {/* Operator dropdown via portal */}
        {openOpKey && createPortal(
          <OpDropdownContent
            activeOp={(colFilters[openOpKey]?.op) || "gt"}
            onSelect={(op) => selectOp(openOpKey, op)}
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          />,
          document.body
        )}

        {/* Filter panel via portal */}
        {openFilterKey && createPortal(
          <div data-filter-panel className={classes.filterPanel}
            style={{ top: filterPanelPos.top, left: filterPanelPos.left }}>
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body
        )}

      </div>
    </div>
  );
}
